// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Phone from "@insite/mobius/Icons/Phone";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSalesPersonByType } from "@werner/App/Store/Selectors/SessionSelectors";
import { SalesPerson } from "@werner/App/Store/Types/SalesPerson";
import Email from "@werner/XDL/Icons/Email";
import { mergeWithCss, xMask } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface SalesPersonCardStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    title?: TypographyPresentationProps;
    wrapper?: GridContainerProps;
    name?: TypographyPresentationProps;
    phone?: LinkPresentationProps;
    email?: LinkPresentationProps;
}

interface OwnProps {
    title?: string;
    salesPersonType?: "inside" | "outside";
    salesPerson?: SalesPerson;
    extendedStyles?: SalesPersonCardStyles;
}

const salesPersonCardStyles: SalesPersonCardStyles = {
    container: {
        gap: 8,
    },
    gridItem: {
        width: 12,
    },
    title: {
        size: 15,
        weight: "bold",
        color: "text.main",
        css: css`
            line-height: 20px;
        `,
    },
    wrapper: {
        gap: 4,
    },
    name: {
        size: 15,
        weight: 400,
        css: css`
            letter-spacing: 0;
            line-height: 17px;
        `,
    },
    email: {
        color: "secondary",
        icon: {
            iconProps: {
                size: 15,
                src: Email,
                color: "common.ui3",
                css: css`
                    margin-top: 3px;
                `,
            },
        },
        typographyProps: {
            size: 15,
            weight: 400,
            css: css`
                letter-spacing: 0;
                line-height: 20px;
            `,
        },
        css: css`
            align-items: flex-start;
        `,
    },
    phone: {
        color: "secondary",
        icon: {
            iconProps: {
                size: 15,
                src: Phone,
                color: "common.ui3",
            },
        },
        typographyProps: {
            size: 15,
            weight: 400,
            css: css`
                letter-spacing: 0;
                line-height: 20px;
            `,
        },
    },
};

const SalesPersonCard: FC<OwnProps> = ({ title, salesPersonType, salesPerson, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(salesPersonCardStyles, extendedStyles));

    const { insideSalesPerson, outsideSalesPerson } = useAppSelector(state => ({
        insideSalesPerson: getSalesPersonByType(state, "inside"),
        outsideSalesPerson: getSalesPersonByType(state, "outside"),
    }));

    if (!salesPersonType) {
        return null;
    }

    let salesPersonData = salesPerson;

    if (!salesPersonData) {
        salesPersonData = salesPersonType === "inside" ? insideSalesPerson : outsideSalesPerson;
    }

    if (!salesPersonData || (!salesPersonData.email && !salesPersonData.name && !salesPersonData.phone)) {
        return null;
    }

    let renderedTitle = title;
    if (!renderedTitle) {
        renderedTitle = salesPersonType === "inside" ? translate("Inside Rep") : translate("Outside Rep");
    }
    return (
        <GridContainer {...styles.container} data-test-selector={`${salesPersonType}-sales-person`}>
            <GridItem {...styles.gridItem}>
                <Typography {...styles.title}>{renderedTitle}</Typography>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <GridContainer {...styles.wrapper}>
                    {salesPersonData.name && (
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.name}>{salesPersonData.name}</Typography>
                        </GridItem>
                    )}
                    {salesPersonData.email && (
                        <GridItem {...styles.gridItem}>
                            <Link href={`mailto:${salesPersonData.email}`} target="_blank" {...styles.email} aria-label={`Sales Person Email:${salesPersonData.email}`}>
                                {salesPersonData.email}
                            </Link>
                        </GridItem>
                    )}
                    {salesPersonData.phone && (
                        <GridItem {...styles.gridItem}>
                            <Link href={`tel:${salesPersonData.phone}`} target="_blank" {...styles.phone} aria-label={`Sales Person Phone:${salesPersonData.phone}`}>
                                {xMask.phoneNumber(salesPersonData.phone)}
                            </Link>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default SalesPersonCard;
